import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Comic } from '../../types';
import ComicCard from '../cards/ComicCard';
import SortableComicCard from '../cards/SortableComicCard';
import { useSettings } from '../../contexts/SettingsContext';
import { ChevronDownIcon, Bars3BottomLeftIcon } from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { reorderUserComics } from '../../api';

export type SortOption = 'series_title' | 'title' | 'published_date' | 'created_at' | 'updated_at' | 'custom';

interface ComicGridProps {
  comics: Comic[];
  onComicUpdate: () => void;
  onEditClick: (comic: Comic) => void;
  readOnly?: boolean;
  // Optional external search/filter state
  externalSearchQuery?: string;
  externalFilter?: 'all' | 'owned' | 'wishlist' | 'ordered' | 'loaned';
  // Sorting props
  showSorting?: boolean;
  externalSortBy?: SortOption;
  onSortChange?: (sortBy: SortOption) => void;
  onCustomReorder?: () => void;
}

const ComicGrid: React.FC<ComicGridProps> = ({ 
  comics, 
  onComicUpdate, 
  onEditClick, 
  readOnly = false, 
  externalSearchQuery, 
  externalFilter,
  showSorting = false,
  externalSortBy,
  onSortChange,
  onCustomReorder
}) => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [filter, setFilter] = useState<'all' | 'owned' | 'wishlist' | 'ordered' | 'loaned'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('title');
  const [isReordering, setIsReordering] = useState(false);
  const [localComics, setLocalComics] = useState<Comic[]>([]);

  // Use external state if provided, otherwise use internal state
  const activeFilter = externalFilter ?? filter;
  const activeSearchQuery = externalSearchQuery ?? searchQuery;
  const activeSortBy = externalSortBy ?? sortBy;
  const showSearchAndFilters = externalSearchQuery === undefined && externalFilter === undefined;

  // Get showAdultContent from settings context, fallback to localStorage for unauthenticated users
  const showAdultContent = settings?.show_adult_content ?? (localStorage.getItem('showAdultContent') === 'true');

  // Clear localComics when the comics prop changes (e.g., after fetchComics)
  // This ensures we use the fresh data from the server instead of stale local state
  useEffect(() => {
    if (!isReordering) {
      setLocalComics([]);
    }
  }, [comics, isReordering]);

  const sortedAndFilteredComics = useMemo(() => {
    const filtered = comics.filter(comic => {
      const matchesFilter = activeFilter === 'all' || comic.status === activeFilter;
      const matchesSearch = activeSearchQuery === '' || 
                           comic.title.toLowerCase().includes(activeSearchQuery.toLowerCase()) ||
                           comic.series?.toLowerCase().includes(activeSearchQuery.toLowerCase()) ||
                           comic.issue_number?.toLowerCase().includes(activeSearchQuery.toLowerCase()) ||
                           comic.language?.name?.toLowerCase().includes(activeSearchQuery.toLowerCase()) ||
                           comic.loaned_to?.toLowerCase().includes(activeSearchQuery.toLowerCase()) ||
                           comic.notes?.toLowerCase().includes(activeSearchQuery.toLowerCase()) ||
                           comic.tags.some(tag => tag.name.toLowerCase().includes(activeSearchQuery.toLowerCase())) ||
                           comic.people.some(person => person.name.toLowerCase().includes(activeSearchQuery.toLowerCase()));
      const matchesAdultContent = showAdultContent || !comic.is_adult_content;
      return matchesFilter && matchesSearch && matchesAdultContent;
    });

    // Sort the filtered comics
    if (activeSortBy === 'custom') {
      // For custom sorting, preserve the original order by maintaining the relative positions
      // of comics that match the filter/search criteria
      const originalIndexMap = new Map(comics.map((comic, index) => [comic.id, index]));
      return filtered.sort((a, b) => {
        const indexA = originalIndexMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
        const indexB = originalIndexMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
        return indexA - indexB;
      });
    }

    return filtered.sort((a, b) => {
      switch (activeSortBy) {
        case 'series_title':
          // Sort by series first, then by title
          const seriesA = a.series || '';
          const seriesB = b.series || '';
          if (seriesA !== seriesB) {
            return seriesA.localeCompare(seriesB);
          }
          return a.title.localeCompare(b.title);
        
        case 'title':
          return a.title.localeCompare(b.title);
        
        case 'published_date':
          const dateA = a.publication_date ? new Date(a.publication_date).getTime() : 0;
          const dateB = b.publication_date ? new Date(b.publication_date).getTime() : 0;
          return dateB - dateA; // Most recent first
        
        case 'created_at':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        
        case 'updated_at':
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        
        default:
          return 0;
      }
    });
  }, [comics, activeFilter, activeSearchQuery, activeSortBy, showAdultContent]);

  const handleSortChange = (newSortBy: SortOption) => {
    if (onSortChange) {
      onSortChange(newSortBy);
    } else {
      setSortBy(newSortBy);
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 500,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for custom sorting
  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (!over || active.id === over.id || activeSortBy !== 'custom') {
      return;
    }

    const oldIndex = sortedAndFilteredComics.findIndex(comic => comic.id.toString() === active.id);
    const newIndex = sortedAndFilteredComics.findIndex(comic => comic.id.toString() === over.id);
    
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedFilteredComics = arrayMove(sortedAndFilteredComics, oldIndex, newIndex);
    setLocalComics(reorderedFilteredComics);
    setIsReordering(true);

    // Scroll to the dropped item after a short delay to allow DOM update
    const scrollToDroppedItem = () => {
      setTimeout(() => {
        const droppedElement = document.querySelector(`[data-id="${active.id}"]`);
        if (droppedElement) {
          droppedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 100);
    };

    try {
      // Calculate new order indices that preserve the order of comics not currently visible
      // due to filtering/search, while only changing the position of the dragged comic
      const isFiltered = activeSearchQuery !== '' || activeFilter !== 'all';
      
      if (isFiltered) {
        // When filtering is active, we need a careful approach:
        // 1. Keep the relative order of all non-visible comics unchanged
        // 2. Only move the dragged comic to its new position among visible comics
        // 3. Maintain the overall structure of the list
        
        const draggedComicId = parseInt(active.id);
        const targetComicId = parseInt(over.id);
        
        // Create a working copy of the original comics array
        const newOrderComics = [...comics];
        
        // Find the current positions
        const draggedIndex = newOrderComics.findIndex(c => c.id === draggedComicId);
        const targetIndex = newOrderComics.findIndex(c => c.id === targetComicId);
        
        if (draggedIndex !== -1 && targetIndex !== -1) {
          // Remove the dragged comic from its current position
          const [draggedComic] = newOrderComics.splice(draggedIndex, 1);
          
          // Calculate the new insertion position
          // If moving down in the filtered list, insert after target
          // If moving up in the filtered list, insert before target
          const adjustedTargetIndex = draggedIndex < targetIndex ? targetIndex : targetIndex;
          const insertIndex = oldIndex < newIndex ? adjustedTargetIndex + 1 : adjustedTargetIndex;
          
          // Insert the dragged comic at the new position
          newOrderComics.splice(insertIndex, 0, draggedComic);
          
          // Create reorder data for all comics
          const reorderData = {
            comic_orders: newOrderComics.map((comic, index) => ({
              comic_id: comic.id,
              order_index: index
            }))
          };
          
          await reorderUserComics(reorderData);
        }
      } else {
        // When no filtering is active, use the simpler approach
        const reorderData = {
          comic_orders: reorderedFilteredComics.map((comic, index) => ({
            comic_id: comic.id,
            order_index: index
          }))
        };
        
        await reorderUserComics(reorderData);
      }
      
      if (onCustomReorder) {
        onCustomReorder();
      }
      
      // Scroll to the dropped item
      scrollToDroppedItem();
    } catch (error) {
      // Revert the order on error
      setLocalComics(sortedAndFilteredComics);
    } finally {
      setIsReordering(false);
    }
  };

  // Use local comics for custom sorting when reordering, otherwise use sorted comics
  const displayComics = activeSortBy === 'custom' && localComics.length > 0 ? localComics : sortedAndFilteredComics;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {showSearchAndFilters && (
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder={t('placeholder.searchComics')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
                  aria-label={t('common.clear')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {!readOnly && (
              <div className="flex gap-2">
                {(['all', 'owned', 'wishlist', 'ordered', 'loaned'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`px-4 py-2 rounded-md capitalize ${
                      filter === status
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {status === 'all' ? t('common.all') : t(`status.${status}`)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showSorting && (
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {displayComics.length} {displayComics.length === 1 ? t('common.comic') : t('common.comics')}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Bars3BottomLeftIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('common.sortBy')}</span>
            </div>
            <div className="relative">
              <select
                value={activeSortBy}
                onChange={(e) => handleSortChange(e.target.value as SortOption)}
                className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 pr-8 text-sm text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="series_title">{t('sort.seriesTitle')}</option>
                <option value="title">{t('sort.title')}</option>
                <option value="published_date">{t('sort.publishedDate')}</option>
                <option value="created_at">{t('sort.dateAdded')}</option>
                <option value="updated_at">{t('sort.lastModified')}</option>
                <option value="custom">{t('sort.custom')}</option>
              </select>
              <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      )}


      {displayComics.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400 text-lg">
            {comics.length === 0 ? t('common.noComicsInCollection') : t('common.noComicsMatchSearch')}
          </div>
        </div>
      ) : activeSortBy === 'custom' && !readOnly ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={displayComics.map(comic => comic.id.toString())}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {displayComics.map((comic) => (
                <div key={comic.id} data-id={comic.id}>
                  <SortableComicCard
                    comic={comic}
                    onComicUpdate={onComicUpdate}
                    onEditClick={onEditClick}
                    readOnly={readOnly}
                    isDragDisabled={isReordering}
                  />
                </div>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {displayComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} onComicUpdate={onComicUpdate} onEditClick={onEditClick} readOnly={readOnly} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ComicGrid;